"use client";

import { useState } from "react";
import type { ServiceSuggestion } from "@/modules/catalog/surfaces";
import { ActionSubmit, Banner, FIELD_INPUT, FIELD_LABEL } from "./ui";
import { useFormAction, type FormAction } from "./useFormAction";

/**
 * ServicesScreen (docs/ux/screens/ServicesScreen.md), shared by both frontends
 * (R22 T14).
 *
 * The three commands arrive as props. Nothing else moved: the fields keep their
 * names, the hidden `serviceId` keeps carrying the row, and `FormData` is still
 * the payload, so this is the same screen on either host rather than two
 * screens that resemble each other.
 *
 * A Service is the thing that unlocks a clinic's tools (J2#2), which is why the
 * empty state says so rather than apologising.
 */
export interface ServiceCommands {
  add: FormAction;
  edit: FormAction;
  remove: FormAction;
}

export interface ServiceView {
  id: string;
  name: string;
  priceDisplay: string;
  priceInput: string;
  durationDisplay: string;
  durationInput: string;
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-sm text-danger">
      {message}
    </p>
  );
}

export function ServicesManager({
  services,
  suggestions,
  commands,
}: {
  services: ServiceView[];
  suggestions: ServiceSuggestion[];
  commands: ServiceCommands;
}) {
  return (
    <>
      <AddServiceCard suggestions={suggestions} add={commands.add} />
      {services.length === 0 ? (
        <p className="text-sm text-muted">
          No services yet. Add your first service to unlock your business tools.
        </p>
      ) : (
        <ul className="space-y-3">
          {services.map((service) => (
            <ServiceRow
              key={service.id}
              service={service}
              commands={commands}
            />
          ))}
        </ul>
      )}
    </>
  );
}

function AddServiceCard({
  suggestions,
  add,
}: {
  suggestions: ServiceSuggestion[];
  add: FormAction;
}) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("");
  // Clear the form after a successful add (ServicesScreen element 6), inside
  // the command rather than in an effect (react-hooks/set-state-in-effect).
  const { state, pending, onSubmit } = useFormAction(async (form) => {
    const result = await add(form);
    if (result.success) {
      setName("");
      setPrice("");
      setDuration("");
    }
    return result;
  });

  return (
    <div className="mb-6 rounded-xl border border-line bg-surface p-6 shadow-sm">
      <h2 className="mb-3 font-bold text-ink">Add a service</h2>

      {suggestions.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-sm text-muted">Suggestions for your categories:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.name}
                type="button"
                onClick={() => {
                  setName(suggestion.name);
                  setDuration(String(suggestion.durationMinutes));
                }}
                className="rounded-lg border border-line bg-page px-3 py-1 text-sm text-body hover:border-accent hover:text-accent"
              >
                {suggestion.name} · {suggestion.durationMinutes} min
              </button>
            ))}
          </div>
        </div>
      )}

      {state.formError && <Banner>{state.formError}</Banner>}
      <form onSubmit={onSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className={FIELD_LABEL}>
            Service name
          </label>
          <input
            id="name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Initial Assessment"
            aria-invalid={!!state.fieldErrors?.name}
            className={FIELD_INPUT}
          />
          <FieldError id="name-error" message={state.fieldErrors?.name} />
        </div>
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="price" className={FIELD_LABEL}>
              Price (USD)
            </label>
            <input
              id="price"
              name="price"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="30"
              aria-invalid={!!state.fieldErrors?.price}
              className={FIELD_INPUT}
            />
            <FieldError id="price-error" message={state.fieldErrors?.price} />
          </div>
          <div>
            <label
              htmlFor="durationMinutes"
              className={FIELD_LABEL}
            >
              Duration (minutes)
            </label>
            <input
              id="durationMinutes"
              name="durationMinutes"
              inputMode="numeric"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="60"
              aria-invalid={!!state.fieldErrors?.durationMinutes}
              className={FIELD_INPUT}
            />
            <FieldError
              id="durationMinutes-error"
              message={state.fieldErrors?.durationMinutes}
            />
          </div>
        </div>
        <ActionSubmit pendingLabel="Adding…" pending={pending}>
          Add service
        </ActionSubmit>
      </form>
    </div>
  );
}

function ServiceRow({
  service,
  commands,
}: {
  service: ServiceView;
  commands: ServiceCommands;
}) {
  const [mode, setMode] = useState<"view" | "edit" | "confirm-remove">("view");
  // Close the row when a save lands (ServicesScreen element 8).
  const edit = useFormAction(async (form) => {
    const result = await commands.edit(form);
    if (result.success) setMode("view");
    return result;
  });
  const remove = useFormAction(commands.remove);

  return (
    <li className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      {edit.state.formError && <Banner>{edit.state.formError}</Banner>}
      {remove.state.formError && <Banner>{remove.state.formError}</Banner>}

      {mode !== "edit" && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-ink">{service.name}</p>
            <p className="text-sm text-muted">
              {service.durationDisplay} · {service.priceDisplay}
            </p>
          </div>
          {mode === "view" && (
            <div className="flex shrink-0 gap-3 text-sm font-medium">
              <button
                type="button"
                onClick={() => setMode("edit")}
                className="text-accent"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setMode("confirm-remove")}
                className="text-danger"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      )}

      {mode === "confirm-remove" && (
        <form
          onSubmit={remove.onSubmit}
          className="mt-3 rounded-lg border border-line bg-page p-4"
        >
          <input type="hidden" name="serviceId" value={service.id} />
          <p className="mb-3 text-sm text-body">
            Remove <span className="font-semibold">{service.name}</span> from your
            services?
          </p>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={remove.pending}
              className="rounded-lg bg-danger px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {remove.pending ? "Removing…" : "Remove service"}
            </button>
            <button
              type="button"
              onClick={() => setMode("view")}
              className="text-sm font-medium text-muted hover:text-ink"
            >
              Keep it
            </button>
          </div>
        </form>
      )}

      {mode === "edit" && (
        <form onSubmit={edit.onSubmit}>
          <input type="hidden" name="serviceId" value={service.id} />
          <div className="mb-3">
            <label
              htmlFor={`name-${service.id}`}
              className={FIELD_LABEL}
            >
              Service name
            </label>
            <input
              id={`name-${service.id}`}
              name="name"
              defaultValue={edit.state.values?.name ?? service.name}
              aria-invalid={!!edit.state.fieldErrors?.name}
              className={FIELD_INPUT}
            />
            <FieldError
              id={`name-${service.id}-error`}
              message={edit.state.fieldErrors?.name}
            />
          </div>
          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor={`price-${service.id}`}
                className={FIELD_LABEL}
              >
                Price (USD)
              </label>
              <input
                id={`price-${service.id}`}
                name="price"
                inputMode="decimal"
                defaultValue={edit.state.values?.price ?? service.priceInput}
                aria-invalid={!!edit.state.fieldErrors?.price}
                className={FIELD_INPUT}
              />
              <FieldError
                id={`price-${service.id}-error`}
                message={edit.state.fieldErrors?.price}
              />
            </div>
            <div>
              <label
                htmlFor={`duration-${service.id}`}
                className={FIELD_LABEL}
              >
                Duration (minutes)
              </label>
              <input
                id={`duration-${service.id}`}
                name="durationMinutes"
                inputMode="numeric"
                defaultValue={edit.state.values?.durationMinutes ?? service.durationInput}
                aria-invalid={!!edit.state.fieldErrors?.durationMinutes}
                className={FIELD_INPUT}
              />
              <FieldError
                id={`duration-${service.id}-error`}
                message={edit.state.fieldErrors?.durationMinutes}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ActionSubmit pendingLabel="Saving…" pending={edit.pending}>
              Save
            </ActionSubmit>
            <button
              type="button"
              onClick={() => setMode("view")}
              className="shrink-0 text-sm font-medium text-muted hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
